import { useCallback } from 'react';

import { UpdatePieces, useCreateMailpiecesListCommentMutation, useUpdatePiecesMutation } from '@api/piece';
import { Piece, PieceAction } from '@apiContract';

import { ValidationData } from '../types';
import { buildValidationPieces } from '../utils';

type FinishActionParams = {
  action: PieceAction;
  pieces: Piece[];
  comment: string;
  notifyMailroom: boolean;
  skipNotifyRecipient?: boolean;
  validationData?: ValidationData;
  additionalData?: Partial<Omit<UpdatePieces, 'pieces' | 'action' | 'shouldNotifyRecipient'>>;
};

export const useFinishPieceMutation = () => {
  const [updatePieces, { isLoading: isUpdating }] = useUpdatePiecesMutation();
  const [createComment, { isLoading: isCommenting }] = useCreateMailpiecesListCommentMutation();

  const runAction = useCallback(
    async ({
      action,
      pieces,
      comment,
      notifyMailroom,
      skipNotifyRecipient,
      validationData,
      additionalData,
    }: FinishActionParams) => {
      await updatePieces({
        pieces: buildValidationPieces(pieces, validationData),
        action,
        ...(skipNotifyRecipient !== undefined && { shouldNotifyRecipient: !skipNotifyRecipient }),
        ...additionalData,
      }).unwrap();

      const trimmedComment = comment.trim();

      if (trimmedComment.length) {
        await createComment({
          text: trimmedComment,
          notifyMailroom,
          pieceIds: pieces.map((piece) => piece.id),
        }).unwrap();
      }
    },
    [updatePieces, createComment],
  );

  return { runAction, isLoading: isUpdating || isCommenting };
};